import { ClientTask, ClientWork, DashboardClient, TaskKind } from './types';

export type WorkloadColumn = 'overdue' | TaskKind;

/** `task`: what one task in the column is called, singular and plural, for "3 clients with 8 assessments due". */
export const WORKLOAD_COLUMNS: { key: WorkloadColumn; label: string; task: [string, string] }[] = [
  { key: 'overdue', label: 'Overdue', task: ['overdue milestone', 'overdue milestones'] },
  { key: 'report', label: 'Reports', task: ['report', 'reports'] },
  { key: 'assessment', label: 'Assessments', task: ['assessment', 'assessments'] },
  { key: 'session', label: 'Sessions to book', task: ['session to book', 'sessions to book'] },
  { key: 'checkin', label: 'Check-ins', task: ['check-in', 'check-ins'] },
  { key: 'success', label: 'Success updates', task: ['success update', 'success updates'] },
  { key: 'renewal', label: 'Renewals', task: ['renewal', 'renewals'] },
];

export const plural = (n: number, [one, many]: [string, string]) => `${n} ${n === 1 ? one : many}`;

export interface CoachWorkload {
  /** Lead coach id, or null for clients without one. */
  coachId: string | null;
  coachName: string;
  location: string;
  activeClients: number;
  /** Clients with at least one task in each column: a client with three assessments due counts once. */
  counts: Record<WorkloadColumn, number>;
  /** The tasks behind those clients, for the drill-down ("3 clients with 8 assessments due"). */
  taskCounts: Record<WorkloadColumn, number>;
  /** Clients with any open task. */
  open: number;
  /** Every open task (overdue is a subset of the other columns, so it is not added in). */
  openTasks: number;
  work: ClientWork[];
}

export interface WorkloadGroup {
  location: string;
  coaches: CoachWorkload[];
  /** Clients per column and in total; each client has one lead coach, so coach rows add up without double counting. */
  totals: Record<WorkloadColumn, number> & { open: number; openTasks: number };
}

const emptyCounts = (): Record<WorkloadColumn, number> => ({ overdue: 0, report: 0, assessment: 0, session: 0, checkin: 0, success: 0, renewal: 0 });

/** Overdue counts milestones only; success updates have their own column and would otherwise swamp it. */
export const taskInColumn = (task: ClientTask, column: WorkloadColumn) =>
  column === 'overdue' ? task.urgency === 'overdue' && task.kind !== 'success' : task.kind === column;

/**
 * Clients with open work per lead coach, by kind of task, grouped by the
 * location most of their clients train at. Coaches with active clients but
 * nothing open still appear.
 */
export function buildWorkload(clients: DashboardClient[], work: ClientWork[]): WorkloadGroup[] {
  const byCoach = new Map<string, CoachWorkload & { places: Map<string, number> }>();
  const keyOf = (client: DashboardClient) => client.coachId ?? '__none';
  const entryFor = (client: DashboardClient) => {
    const key = keyOf(client);
    let entry = byCoach.get(key);
    if (!entry) {
      entry = {
        coachId: client.coachId,
        coachName: client.coachId ? client.coachName : 'No lead coach',
        location: '',
        activeClients: 0,
        counts: emptyCounts(),
        taskCounts: emptyCounts(),
        open: 0,
        openTasks: 0,
        work: [],
        places: new Map(),
      };
      byCoach.set(key, entry);
    }
    return entry;
  };

  clients.forEach((client) => {
    const entry = entryFor(client);
    if (client.activeProgramme) entry.activeClients += 1;
    entry.places.set(client.location, (entry.places.get(client.location) ?? 0) + 1);
  });
  work.forEach((item) => {
    const entry = entryFor(item.client);
    entry.work.push(item);
    entry.open += 1;
    entry.openTasks += item.tasks.length;
    WORKLOAD_COLUMNS.forEach(({ key }) => {
      const inColumn = item.tasks.filter((task) => taskInColumn(task, key)).length;
      entry.taskCounts[key] += inColumn;
      if (inColumn > 0) entry.counts[key] += 1;
    });
  });

  const groups = new Map<string, CoachWorkload[]>();
  byCoach.forEach(({ places, ...entry }) => {
    if (entry.activeClients === 0 && entry.open === 0) return;
    const location = Array.from(places.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0]?.[0] ?? 'No location';
    groups.set(location, [...(groups.get(location) ?? []), { ...entry, location }]);
  });

  return Array.from(groups.entries())
    .map(([location, coaches]) => {
      const sorted = coaches.sort((a, b) => b.open - a.open || b.counts.overdue - a.counts.overdue || a.coachName.localeCompare(b.coachName));
      const totals = { ...emptyCounts(), open: 0, openTasks: 0 };
      sorted.forEach((coach) => {
        WORKLOAD_COLUMNS.forEach(({ key }) => (totals[key] += coach.counts[key]));
        totals.open += coach.open;
        totals.openTasks += coach.openTasks;
      });
      return { location, coaches: sorted, totals };
    })
    .sort((a, b) => a.location.localeCompare(b.location));
}

/** 0–4 heat level for a count, relative to the busiest cell on screen so the scale works for one location or all. */
export const heatLevel = (count: number, max: number) => (count <= 0 || max <= 0 ? 0 : Math.max(1, Math.ceil((4 * count) / max)));
